import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { ClerkNotConfigured } from "@/components/ClerkNotConfigured";
import { clerkEnabled } from "@/services/clerk";
import { REDIRECT_PARAM } from "@/lib/redirects";
import { FullScreenLoader } from "@/components/Loader";

/**
 * Auth boundary. Clerk is the source of truth: wait for it to load (so a
 * reload doesn't flash-redirect), then require a signed-in session.
 *
 * Most of the app no longer needs one. The directory has a preview anybody may
 * see, so a stranger following a link from Soft Network's site finds the thing
 * rather than a sign-in form — the server decides what they are shown, and a
 * signed-out viewer gets exactly what a signed-in non-member gets.
 *
 * `requireAccount` marks the pages where there is genuinely nothing to show
 * without one: your own profile, your billing, applying, the admin. They are
 * named one at a time rather than defaulted, because "everything is open
 * unless somebody remembered" is how a paid-only page ends up public.
 */
function RequireSession({
  children,
  requireAccount,
}: {
  children: ReactNode;
  requireAccount: boolean;
}) {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();

  if (!isLoaded) {
    // Matches the members-area shell's own loading state.
    return (
      <FullScreenLoader label="Loading…" />
    );
  }

  if (!isSignedIn && requireAccount) {
    // Clerk's own parameter name, because Clerk's components are what read it.
    // The search string comes along: a link to a filtered directory should
    // survive signing in, not just the path.
    const target = encodeURIComponent(location.pathname + location.search);
    // Registering is the one of these somebody reaches without an account
    // already. Every other account-only page — profile, billing, your
    // legacies — belongs to a member who has one, so sending them to a
    // sign-up form would be asking them to make a second.
    const door = location.pathname.startsWith("/apply") ? "/sign-up" : "/sign-in";
    return <Navigate to={`${door}?${REDIRECT_PARAM}=${target}`} replace />;
  }

  return <>{children}</>;
}

export function ProtectedRoute({
  children,
  requireAccount = false,
}: {
  children: ReactNode;
  requireAccount?: boolean;
}) {
  if (!clerkEnabled) return <ClerkNotConfigured />;
  return <RequireSession requireAccount={requireAccount}>{children}</RequireSession>;
}

export default ProtectedRoute;
